import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Plus, 
  Wifi,
  Smartphone,
  Zap,
  Droplets,
  ChevronRight,
  Receipt,
  Globe,
  Clock,
  CalendarClock
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

// Provider config with logos
const providerConfig = {
  "flow_ja": { logo: "/flow-logo.png", name: "Flow", bgColor: "bg-white", borderColor: "border-blue-500/30" },
  "digicel_ja": { logo: "/digicel-logo.png", name: "Digicel", bgColor: "bg-white", borderColor: "border-red-500/30" },
  "jps_ja": { logo: "/jps-logo.png", name: "JPS", bgColor: "bg-white", borderColor: "border-yellow-500/30" },
  "nwc_ja": { logo: "/nwc-logo.png", name: "NWC", bgColor: "bg-white", borderColor: "border-cyan-500/30" },
  "jps_diaspora": { logo: "/jps-logo.png", name: "JPS", bgColor: "bg-white", borderColor: "border-yellow-500/30" },
  "nwc_diaspora": { logo: "/nwc-logo.png", name: "NWC", bgColor: "bg-white", borderColor: "border-cyan-500/30" },
  "flow_diaspora": { logo: "/flow-logo.png", name: "Flow", bgColor: "bg-white", borderColor: "border-blue-500/30" },
  "digicel_diaspora": { logo: "/digicel-logo.png", name: "Digicel", bgColor: "bg-white", borderColor: "border-red-500/30" },
};

// Fallback icons for providers without logos
const providerIcons = {
  "flow_ja": { icon: Wifi, color: "bg-blue-500", textColor: "text-blue-500" },
  "digicel_ja": { icon: Smartphone, color: "bg-red-500", textColor: "text-red-500" },
  "jps_ja": { icon: Zap, color: "bg-yellow-500", textColor: "text-yellow-500" },
  "nwc_ja": { icon: Droplets, color: "bg-cyan-500", textColor: "text-cyan-500" },
  "jps_diaspora": { icon: Zap, color: "bg-yellow-500", textColor: "text-yellow-500" },
  "nwc_diaspora": { icon: Droplets, color: "bg-cyan-500", textColor: "text-cyan-500" },
  "flow_diaspora": { icon: Wifi, color: "bg-blue-500", textColor: "text-blue-500" },
  "digicel_diaspora": { icon: Smartphone, color: "bg-red-500", textColor: "text-red-500" },
};

const Bills = ({ user }) => {
  const navigate = useNavigate();

  // Fetch user's saved billers
  const { data: myBillers, isLoading: loadingBillers } = useQuery({
    queryKey: ["myBillers"],
    queryFn: async () => {
      const response = await axios.get(`${API}/bills/my-billers`);
      return response.data;
    }
  });

  // Fetch available providers
  const { data: providers } = useQuery({
    queryKey: ["billProviders"],
    queryFn: async () => {
      const response = await axios.get(`${API}/bills/providers`);
      return response.data;
    }
  });

  // Fetch scheduled payments
  const { data: scheduledPayments } = useQuery({
    queryKey: ["scheduledPayments"],
    queryFn: async () => {
      const response = await axios.get(`${API}/bills/schedules`);
      return response.data;
    }
  });

  // Filter local (Jamaica) billers only
  const localBillers = myBillers?.filter(b => !b.is_diaspora) || [];

  const formatCurrency = (amount) => {
    return `J$${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    const days = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="bills-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Bills</h1>
      </header>

      <main className="px-5 max-w-lg mx-auto">
        {/* Diaspora Bills Banner */}
        <button
          onClick={() => navigate("/bills/diaspora")}
          className="w-full glass rounded-2xl p-4 mb-6 flex items-center gap-4 
            hover:bg-white/10 transition-colors bg-gradient-to-r from-primary/10 to-primary/5"
          data-testid="diaspora-bills-btn"
        >
          <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
            <Globe className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium text-foreground">Diaspora Bills</p>
            <p className="text-xs text-muted-foreground">Pay Jamaica bills from abroad with USD</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Scheduled Payments */}
        {scheduledPayments && scheduledPayments.length > 0 && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-3">
              Scheduled Payments
            </p>
            <div className="space-y-2">
              {scheduledPayments.slice(0, 3).map((schedule) => (
                <div
                  key={schedule.schedule_id}
                  className="glass rounded-xl p-3 flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                    <CalendarClock className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {schedule.provider_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {schedule.schedule_type === "once" ? "One-time" : 
                       schedule.schedule_type === "weekly" ? "Weekly" : "Monthly"} • 
                      Next: {new Date(schedule.next_payment_date).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="font-mono text-sm text-foreground">
                    {formatCurrency(schedule.amount)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Bills Section */}
        {localBillers && localBillers.length > 0 && (
          <div className="mb-8">
            <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-4">
              My Bills
            </p>
            <div className="space-y-3">
              {localBillers.map((biller) => {
                const logoConfig = providerConfig[biller.provider_id];
                const iconConfig = providerIcons[biller.provider_id] || { 
                  icon: Receipt, 
                  color: "bg-gray-500",
                  textColor: "text-gray-500"
                };
                const Icon = iconConfig.icon;
                const daysUntilDue = getDaysUntilDue(biller.due_date);
                
                return (
                  <button
                    key={biller.biller_id}
                    onClick={() => navigate(`/bills/${biller.biller_id}`)}
                    data-testid={`biller-${biller.biller_id}`}
                    className="w-full glass rounded-2xl p-4 flex items-center gap-4 
                      hover:bg-white/10 transition-colors text-left"
                  >
                    {logoConfig ? (
                      <div className={`w-12 h-12 rounded-xl ${logoConfig.bgColor} border ${logoConfig.borderColor} flex items-center justify-center p-1.5 overflow-hidden`}>
                        <img 
                          src={logoConfig.logo} 
                          alt={logoConfig.name} 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className={`w-12 h-12 rounded-xl ${iconConfig.color} flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-foreground truncate">
                          {biller.nickname || biller.provider_name}
                        </p>
                        {biller.autopay_enabled && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded bg-primary/20 text-primary">
                            Auto
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {biller.masked_account}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm font-semibold text-foreground">
                        {formatCurrency(biller.outstanding_balance)}
                      </p>
                      {daysUntilDue !== null && (
                        <p className={`text-xs ${
                          daysUntilDue <= 5 ? "text-rose-400" : 
                          daysUntilDue <= 10 ? "text-yellow-400" : "text-muted-foreground"
                        }`}>
                          Due in {daysUntilDue}d
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Add Biller Section */}
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-4">
            {localBillers && localBillers.length > 0 ? "Add Another Biller" : "Add a Biller"}
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            {providers?.map((provider) => {
              const logoConfig = providerConfig[provider.provider_id];
              const iconConfig = providerIcons[provider.provider_id] || { 
                icon: Receipt, 
                color: "bg-gray-500",
                textColor: "text-gray-500"
              };
              const Icon = iconConfig.icon;
              
              return (
                <button
                  key={provider.provider_id}
                  onClick={() => navigate(`/bills/add/${provider.provider_id}`)}
                  data-testid={`provider-${provider.provider_id}`}
                  className="glass rounded-2xl p-4 flex flex-col items-center gap-3 
                    hover:bg-white/10 transition-all hover:scale-105 active:scale-95"
                >
                  {logoConfig ? (
                    <div className={`w-14 h-14 rounded-xl ${logoConfig.bgColor} border ${logoConfig.borderColor} flex items-center justify-center p-2 overflow-hidden`}>
                      <img 
                        src={logoConfig.logo} 
                        alt={logoConfig.name} 
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className={`w-14 h-14 rounded-xl ${iconConfig.color} flex items-center justify-center`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                  )}
                  <span className="text-sm font-medium text-foreground text-center">
                    {logoConfig?.name || provider.name.split(" ")[0]}
                  </span>
                </button>
              );
            })}
            
            {/* Add Custom Biller (Phase 2) */}
            <button
              onClick={() => navigate("/bills/add")}
              data-testid="add-custom-biller"
              className="glass rounded-2xl p-4 flex flex-col items-center gap-3 
                hover:bg-white/10 transition-all border-2 border-dashed border-white/10"
            >
              <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center">
                <Plus className="w-7 h-7 text-muted-foreground" />
              </div>
              <span className="text-sm font-medium text-muted-foreground text-center">
                Other
              </span>
            </button>
          </div>
        </div>

        {/* Empty State */}
        {!loadingBillers && (!localBillers || localBillers.length === 0) && (
          <div className="text-center py-8 mt-4">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm">
              Add your first biller to start paying bills
            </p>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-8 glass rounded-2xl p-4">
          <p className="text-xs text-muted-foreground text-center">
            Pay your JPS, NWC, Flow, and Digicel bills instantly. 
            J$50 fee per transaction. Auto-pay and scheduled payments available.
          </p>
        </div>
      </main>

      <NavBar active="wallet" />
    </div>
  );
};

export default Bills;
